import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleOutlined, ShoppingOutlined, HomeOutlined } from '@ant-design/icons';
import { Button, Typography, Spin, Empty } from 'antd';
import dayjs from 'dayjs';
import { useOrders } from '@hooks';

const { Title, Paragraph } = Typography;

export const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  console.log('OrderSuccessPage - Session ID:', sessionId);
  
  const { getOrderBySessionId } = useOrders();
  const { data: order, isLoading, isError, error } = getOrderBySessionId(sessionId || '');
  
  console.log('OrderSuccessPage - Loading:', isLoading);
  console.log('OrderSuccessPage - Error:', isError, error);
  console.log('OrderSuccessPage - Order data:', order);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: 'bg-orangicide/20 text-orangicide',
      paid: 'bg-green-500/20 text-green-500',
      processing: 'bg-blue-500/20 text-blue-500',
      shipped: 'bg-purple-500/20 text-purple-500',
      delivered: 'bg-green-600/20 text-green-600',
      cancelled: 'bg-red-500/20 text-red-500',
    };
    return statusColors[status.toLowerCase()] || 'bg-gray-500/20 text-gray-500';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <Paragraph className="font-orator text-white text-xl mt-4">
            Loading your order...
          </Paragraph>
        </div>
      </div>
    );
  }

  if (isError || !order || !sessionId) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <Empty
            description={
              <span className="font-orator text-white text-xl">
                Unable to load order details
              </span>
            }
          />
          <Paragraph className="font-orator text-zinc-400 mt-4 mb-6">
            Your order may still be processing. Please check your email for confirmation.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/merch')}
            className="btn-orangicide font-orator text-xl"
          >
            Return to Store
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Success Header with stronger contrast */}
      <section className="bg-gradient-to-b from-zinc-900 via-zinc-900 to-black py-20 border-b-2 border-orangicide/20">
        <div className="container mx-auto px-4 text-center">
          <div className="mb-8 animate-bounce">
            <CheckCircleOutlined 
              className="text-green-500 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]" 
              style={{ fontSize: '100px' }} 
            />
          </div>
          <Title 
            level={1} 
            className="font-displayed !font-normal !text-6xl md:!text-7xl text-orangicide uppercase mb-6 drop-shadow-[0_0_30px_rgba(247,73,2,0.5)]"
          >
            PAYMENT SUCCESSFUL!
          </Title>
          <Paragraph className="font-orator font-bold text-white text-2xl mb-4 uppercase tracking-wider">
            Thank you for your purchase
          </Paragraph>
          <div className="inline-block bg-zinc-800 border-2 border-orangicide px-8 py-4 mt-4">
            <Paragraph className="font-orator text-white text-sm mb-2">
              ORDER NUMBER
            </Paragraph>
            <Paragraph className="font-orator text-orangicide text-3xl font-bold mb-0">
              {order.orderNumber}
            </Paragraph>
          </div>
        </div>
      </section>

      {/* Order Details with improved contrast */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-zinc-900 border-2 border-zinc-700 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orangicide to-orange-600 px-8 py-6">
              <Title level={2} className="font-orator !text-white uppercase mb-0 !text-3xl">
                ORDER DETAILS
              </Title>
            </div>

            {/* Order Information Grid */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-zinc-800 border border-zinc-700 p-5">
                  <Paragraph className="font-orator font-bold text-orangicide text-sm uppercase mb-2">
                    Order Number
                  </Paragraph>
                  <Paragraph className="font-orator font-bold text-white text-xl mb-0">
                    {order.orderNumber}
                  </Paragraph>
                </div>

                <div className="bg-zinc-800 border border-zinc-700 p-5">
                  <Paragraph className="font-orator font-bold text-orangicide text-sm uppercase mb-2">
                    Date
                  </Paragraph>
                  <Paragraph className="font-orator font-bold text-white text-xl mb-0">
                    {dayjs(order.createdAt).format('MMM DD, YYYY')}
                  </Paragraph>
                  <Paragraph className="font-orator font-bold text-white text-xl mb-0">
                    {dayjs(order.createdAt).format('HH:mm')}
                  </Paragraph>
                </div>

                <div className="bg-zinc-800 border border-zinc-700 p-5">
                  <Paragraph className="font-orator font-bold text-orangicide text-sm uppercase mb-2">
                    Email
                  </Paragraph>
                  <Paragraph className="font-orator font-bold text-white text-xl uppercase mb-0 break-all">
                    {order.customerEmail}
                  </Paragraph>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mb-8">
                <Paragraph className="font-orator text-orangicide text-sm uppercase mb-3 font-bold">
                  Status
                </Paragraph>
                <span 
                  className={`inline-block px-6 py-3 ${getStatusColor(order.status)} font-orator font-bold uppercase text-lg border-2 border-current`}
                >
                  {order.status}
                </span>
              </div>

              {/* Items Table with high contrast */}
              <div className="mb-8">
                <Title level={3} className="font-orator text-white uppercase mb-4 !text-2xl">
                  Items Ordered
                </Title>
                <div className="bg-zinc-800 border-2 border-zinc-700 overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-4 bg-zinc-900 px-6 py-4 border-b-2 border-orangicide">
                    <div className="col-span-5 font-orator text-orangicide uppercase font-bold text-sm">
                      Product
                    </div>
                    <div className="col-span-2 font-orator text-orangicide uppercase font-bold text-sm text-center">
                      Quantity
                    </div>
                    <div className="col-span-2 font-orator text-orangicide uppercase font-bold text-sm text-right">
                      Price
                    </div>
                    <div className="col-span-3 font-orator text-orangicide uppercase font-bold text-sm text-right">
                      Subtotal
                    </div>
                  </div>

                  {/* Table Body */}
                  {order.items.map((item, index) => (
                    <div 
                      key={item.id}
                      className={`grid grid-cols-12 gap-4 px-6 py-5 ${
                        index < order.items.length - 1 ? 'border-b border-zinc-700' : ''
                      } hover:bg-zinc-750 transition-colors`}
                    >
                      <div className="col-span-5 font-orator text-white text-lg font-bold">
                        {item.productName}
                      </div>
                      <div className="col-span-2 font-orator text-white text-lg text-center">
                        {item.quantity}
                      </div>
                      <div className="col-span-2 font-orator text-white font-bold text-lg text-right">
                        {formatPrice(item.price)}
                      </div>
                      <div className="col-span-3 font-orator text-white font-bold text-lg text-right">
                        {formatPrice(item.subtotal)}
                      </div>
                    </div>
                  ))}

                  {/* Subtotal */}
                  <div className="border-t border-zinc-700 px-6 py-4">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-9 font-orator text-zinc-400 uppercase font-bold text-lg text-right">
                        Subtotal:
                      </div>
                      <div className="col-span-3 font-orator text-zinc-400 font-bold text-lg text-right">
                        {formatPrice(order.subtotalBeforeDiscount)}
                      </div>
                    </div>
                  </div>

                  {/* Discount (only show if there's a discount) */}
                  {order.discountAmount > 0 && (
                    <div className="px-6 py-4 bg-green-500/10">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-9 font-orator text-green-500 uppercase font-bold text-lg text-right">
                          Tier Discount:
                        </div>
                        <div className="col-span-3 font-orator text-green-500 font-bold text-lg text-right">
                          -{formatPrice(order.discountAmount)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Total */}
                  <div className="bg-orangicide px-6 py-5">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-9 font-orator text-black uppercase font-bold text-2xl text-right">
                        TOTAL:
                      </div>
                      <div className="col-span-3 font-orator text-black font-bold text-2xl text-right">
                        {formatPrice(order.totalAmount)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* What's Next Section */}
              <div className="bg-gradient-to-r from-zinc-800 to-zinc-850 border-2 border-orangicide/30 p-6 mb-8">
                <Paragraph className="font-orator text-orangicide text-xl mb-3 font-bold uppercase">
                  What's next?
                </Paragraph>
                <Paragraph className="font-orator text-white text-base leading-relaxed mb-0">
                  You will receive a confirmation email at{' '}
                  <span className="text-orangicide font-bold">{order.customerEmail}</span>{' '}
                  with your order details and tracking information.
                </Paragraph>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingOutlined />}
                  onClick={() => navigate('/merch')}
                  className="btn-orangicide font-orator text-xl h-16 px-8 shadow-lg shadow-orangicide/30 hover:shadow-orangicide/50 transition-all"
                >
                  Continue Shopping
                </Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<HomeOutlined />}
                  onClick={() => navigate('/')}
                  className="btn-orangicide font-orator text-xl h-16 px-8 shadow-lg shadow-orangicide/30 hover:shadow-orangicide/50 transition-all"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
